/**
 * <ProcessSequence /> — the named process as a numbered visual flow with
 * connectors. Each node surfaces the actor, the tools/handoffs used today, and
 * a highlighted "time leak" tag (where effort leaks in the current world) with
 * a compact per-step minutes chip. A single subtle summary line sums the leak
 * across steps so the ROI's recovered-minutes claim is GROUNDED in this leak.
 */

export interface ProcessStep {
  order: number;
  name: string;
  actor: string;
  toolsHandoffs: string;
  timeLeak: string;
  /** Conservative minutes ONE person loses at this step per run. */
  leakMinutes: number;
}

const roundInt = (x: number): string => Math.round(x).toLocaleString('en-US');

export default function ProcessSequence({ steps }: { steps: ProcessStep[] }) {
  const totalLeakMinutes = steps.reduce(
    (sum, s) => sum + (Number.isFinite(s.leakMinutes) ? s.leakMinutes : 0),
    0,
  );

  return (
    <section className="se-block" aria-label="Process today">
      <h2 className="se-section-title">The process today</h2>
      <ol className="se-flow">
        {steps.map((s, i) => (
          <li className="se-flow__node" key={`${s.order}-${s.name}`}>
            <div className="se-flow__marker">
              <span className="se-flow__num">{s.order}</span>
              {i < steps.length - 1 && <span className="se-flow__connector" aria-hidden="true" />}
            </div>
            <div className="se-flow__body">
              <h3 className="se-flow__name">{s.name}</h3>
              <div className="se-flow__meta">
                <span className="se-flow__actor">{s.actor}</span>
                <span className="se-flow__tools">{s.toolsHandoffs}</span>
              </div>
              <div className="se-flow__leak">
                <span className="se-flow__leak-tag">Time leak</span>
                <span className="se-flow__leak-text">{s.timeLeak}</span>
                <span className="se-flow__leak-mins" data-testid="step-leak-mins">
                  ~{roundInt(s.leakMinutes)} min
                </span>
              </div>
            </div>
          </li>
        ))}
      </ol>
      <p className="se-flow__leak-total" data-testid="process-leak-total">
        ≈ {roundInt(totalLeakMinutes)} min lost per run, per person — across {steps.length} steps
      </p>
    </section>
  );
}
